import { Controller } from "@hotwired/stimulus"
import { getCachedMessages, getOutboxMessages } from "../chat_offline_resources"
import db from "../chat_db"

// Hydrates the chat thread from IndexedDB when offline.
// Also renders outbox (pending) messages at the bottom.
// When online: no-op (server-rendered content is correct).
export default class extends Controller {
  static targets = ["messages", "offlineNotice", "input", "sendButton"]
  static values  = { chatId: Number, currentUserId: Number }

  async connect() {
    const isOffline = localStorage.getItem("chat:online") === "false" || !navigator.onLine
    if (!isOffline) return

    this._disableInput()
    await this._hydrateFromIdb()
    await this._markChatRead()
  }

  // -- private --

  async _hydrateFromIdb() {
    if (!this.hasChatIdValue || !this.hasMessagesTarget) return

    const chatId = this.chatIdValue

    // Show offline notice
    if (this.hasOfflineNoticeTarget) {
      this.offlineNoticeTarget.classList.remove("hidden")
    }

    // Load cached messages
    let messages
    try {
      messages = await getCachedMessages(chatId)
    } catch {
      return
    }

    // Load outbox messages for this chat
    let outboxMsgs
    try {
      outboxMsgs = await getOutboxMessages(chatId)
    } catch {
      outboxMsgs = []
    }

    // Clear and re-render
    const container = this.messagesTarget.querySelector("#chat_messages")
    if (!container) return

    container.innerHTML = messages.map(msg => this._buildBubble(msg, false)).join("")

    // Append pending outbox messages
    if (outboxMsgs.length > 0) {
      container.innerHTML += outboxMsgs.map(msg => this._buildPendingBubble(msg)).join("")
    }

    // Scroll to bottom
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  async _markChatRead() {
    // Update local cache to zero out unread count for this chat
    try {
      await db.cached_chats.update(this.chatIdValue, { unread_count: 0 })
    } catch {
      // IDB not available -- ignore
    }
  }

  _disableInput() {
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
      this.inputTarget.placeholder = "You're offline. Messages will send when you reconnect."
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }
  }

  _buildBubble(msg, isOwn) {
    const isMe = isOwn || (this.hasCurrentUserIdValue && msg.sender_id === this.currentUserIdValue)
    const content = this._esc(msg.content || "")
    const time    = msg.created_at ? new Date(msg.created_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }) : ""

    return `
      <div class="flex ${isMe ? "justify-end" : "justify-start"} mb-2">
        <div class="max-w-[80%]">
          <div class="px-3.5 py-2.5 text-sm break-words leading-relaxed
                      ${isMe ? "bg-sapphire text-white rounded-2xl rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-sm"}">
            <span class="whitespace-pre-wrap">${content}</span>
          </div>
          <p class="text-xs mt-1 px-1 opacity-60 text-slate-400 ${isMe ? "text-right" : ""}">${this._esc(time)}</p>
        </div>
      </div>
    `
  }

  _buildPendingBubble(msg) {
    const content = this._esc(msg.content || "")
    const time = new Date(msg.created_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })

    return `
      <div class="flex justify-end mb-2">
        <div class="max-w-[80%]">
          <div class="px-3.5 py-2.5 text-sm break-words leading-relaxed bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl rounded-br-sm">
            <span class="whitespace-pre-wrap">${content}</span>
          </div>
          <p class="text-xs mt-1 px-1 opacity-60 text-amber-500 text-right">
            Pending &middot; ${this._esc(time)}
          </p>
        </div>
      </div>
    `
  }

  _esc(str) {
    return String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
